import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import cn from "clsx";

import { CheckoutService } from "@/services";
import ErrorLayout from "@/components/errorLayout";
import Loader from "@/components/loader";
import { hideErrorPage, showErrorPage } from "@/redux/actions/common.action";
import { useAppDispatch } from "@/redux/store";
import { useIsRTL, useRouteSlug } from "@/hooks";
import { useTranslation } from "react-i18next";
import { LOADER_TYPES } from "@/constants";

import "./style.scss";

type RequestStatus = "loading" | "success" | "error";

const RetryPaymentPage = () => {
  const { t } = useTranslation("retryPayment");
  const dispatch = useAppDispatch();
  const isRTL = useIsRTL();
  const routeSlug = useRouteSlug();
  const { token } = useParams<{ token: string }>();

  const [status, setStatus] = useState<RequestStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (!token) {
      dispatch(hideErrorPage());
      dispatch(showErrorPage("is404", true));
    }

    return () => {
      dispatch(hideErrorPage());
    };
  }, [token, dispatch]);

  useEffect(() => {
    if (!token) return;

    const fetchPaymentLink = async () => {
      try {
        const response = await CheckoutService.getPaymentRetryLink(token);
        if (response?.success) {
          const paymentLink = response?.data?.paymentLink?.trim();
          if (paymentLink) {
            setStatus("success");
            window.location.href = paymentLink;
          } else {
            setStatus("error");
            setErrorMessage(t("errorDefault"));
          }
        } else {
          setStatus("error");
          setErrorMessage(response?.message || t("errorDefault"));
        }
      } catch (error: unknown) {
        setStatus("error");
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          (error as Error)?.message ||
          t("errorDefault");
        setErrorMessage(message);
      }
    };

    fetchPaymentLink();
  }, [token]);

  if (!token) {
    return (
      <ErrorLayout
        redirectTo={`/${routeSlug}/`}
        buttonLabel="Home"
        forceFullPageRedirect
      />
    );
  }

  return (
    <div
      className={cn("retry-payment-page", { rtl: isRTL })}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="retry-payment-card">
        {["success", "loading"].includes(status) && (
          <div className="retry-payment-content">
            <p className="retry-payment-text">{t("doNotReload")}</p>
            <Loader
              type={LOADER_TYPES.DOTS}
              visible
              fullscreen={false}
              className="retry-payment-loader"
            />
          </div>
        )}
        {status === "error" && (
          <div className="retry-payment-content">
            <p className="retry-payment-result error">{errorMessage}</p>
            <a href={`/${routeSlug}/`} className="retry-payment-continue-btn">
              {t("continueShopping")}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetryPaymentPage;
